import Phaser from 'phaser';
import monstersData from '@/data/monsters.json';
import {
  hasMonsterSprite,
  monsterMoveKey,
  type MonsterDir,
} from '@/data/monsterSpriteRegistry';

interface MonsterTpl {
  tplId: number;
  name: string;
  level: number;
  hp: number;
  spriteKey: string;
}
const TPL: Record<number, MonsterTpl> = Object.fromEntries(
  (monstersData.monsters as MonsterTpl[]).map((m) => [m.tplId, m]),
);

export interface MonsterOpts {
  scene: Phaser.Scene;
  id: number;
  tplId: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  isElite?: boolean;
  scale?: number;
}

export class Monster extends Phaser.GameObjects.Container {
  id: number;
  tplId: number;
  hp: number;
  maxHp: number;
  alive = true;
  sprite!: Phaser.GameObjects.Sprite;
  shadow!: Phaser.GameObjects.Graphics;
  nameTag!: Phaser.GameObjects.Text;
  hpBar!: Phaser.GameObjects.Graphics;
  selectionRing!: Phaser.GameObjects.Graphics;
  aura?: Phaser.GameObjects.Graphics;
  auraTween?: Phaser.Tweens.Tween;
  deathTween?: Phaser.Tweens.Tween;
  isElite = false;
  // Base render scale for monster sprites on the field. Reduced from 1 → 2/3
  // so monster props feel less oversized relative to the player/tile grid.
  static readonly BASE_SCALE = 2 / 3;
  spriteScale = Monster.BASE_SCALE;
  targetX: number;
  targetY: number;
  selected = false;
  private hitTween?: Phaser.Tweens.Tween;
  private hitTintTimer?: Phaser.Time.TimerEvent;
  private spriteBaseX = 0;
  private spriteBaseY = 0;
  private hasAnim = false;
  private animBaseId = '';
  private facing: MonsterDir = 'down';
  private currentAnimState: 'idle' | 'walk' | 'attack' | 'die' = 'idle';

  constructor(opts: MonsterOpts) {
    super(opts.scene, opts.x, opts.y);
    this.id = opts.id; this.tplId = opts.tplId;
    this.hp = opts.hp; this.maxHp = opts.maxHp;
    this.targetX = opts.x; this.targetY = opts.y;
    const tpl = TPL[opts.tplId];
    const wantedSpriteId = tpl?.spriteKey ?? 'mon_goblin';
    // Prefer animated per-monster sheet (move-sheet) when available,
    // otherwise fall back to the procedural fallback texture.
    let tex: string;
    if (hasMonsterSprite(wantedSpriteId) && opts.scene.textures.exists(monsterMoveKey(wantedSpriteId))) {
      tex = monsterMoveKey(wantedSpriteId);
      this.hasAnim = true;
      this.animBaseId = wantedSpriteId;
    } else {
      tex = opts.scene.textures.exists(wantedSpriteId) ? wantedSpriteId : 'mon_goblin';
    }
    this.sprite = opts.scene.add.sprite(0, 0, tex);
    this.sprite.setScale(this.spriteScale);
    if (this.hasAnim) {
      const idleKey = `${this.animBaseId}__idle_${this.facing}`;
      if (opts.scene.anims.exists(idleKey)) this.sprite.play(idleKey);
    }
    this.shadow = opts.scene.add.graphics();
    this.drawShadow();
    this.nameTag = opts.scene.add.text(0, -42, `${tpl?.name ?? 'Monster'} Lv.${tpl?.level ?? '?'}`, {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '12px', color: '#ff8a80', stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5);
    this.hpBar = opts.scene.add.graphics();
    this.selectionRing = opts.scene.add.graphics();
    this.selectionRing.setVisible(false);
    this.add([this.shadow, this.selectionRing, this.sprite, this.nameTag, this.hpBar]);
    this.drawSelectionRing();
    this.drawHp();
    this.setSize(this.sprite.width * this.spriteScale, this.sprite.height * this.spriteScale);
    this.setInteractive(new Phaser.Geom.Rectangle(-(this.sprite.width * this.spriteScale) / 2, -(this.sprite.height * this.spriteScale) / 2, this.sprite.width * this.spriteScale, this.sprite.height * this.spriteScale), Phaser.Geom.Rectangle.Contains);
    opts.scene.add.existing(this);
    this.setDepth(8);
    if (opts.isElite) this.setElite(true, opts.scale ?? 1.3);
  }

  private tickInterp(delta: number) {
    if (!this.scene) return;
    const dxT = this.targetX - this.x;
    const dyT = this.targetY - this.y;
    const distSq = dxT * dxT + dyT * dyT;
    if (distSq < 0.25) {
      // arrived → idle
      if (this.hasAnim && this.alive && this.currentAnimState === 'walk') {
        this.playState('idle');
      }
      return;
    }
    const k = Math.min(1, delta / 120);
    this.x += dxT * k;
    this.y += dyT * k;
    if (this.hasAnim && this.alive) {
      // Update facing from dominant axis.
      const newFacing: MonsterDir =
        Math.abs(dxT) > Math.abs(dyT)
          ? (dxT > 0 ? 'right' : 'left')
          : (dyT > 0 ? 'down' : 'up');
      if (newFacing !== this.facing || this.currentAnimState !== 'walk') {
        this.facing = newFacing;
        this.playState('walk');
      }
    }
  }

  private playState(state: 'idle' | 'walk' | 'attack' | 'die') {
    if (!this.hasAnim) return;
    let key: string;
    if (state === 'die') key = `${this.animBaseId}__die`;
    else key = `${this.animBaseId}__${state}_${this.facing}`;
    if (!this.scene?.anims.exists(key)) return;
    this.currentAnimState = state;
    this.sprite.play(key, true);
  }

  setHp(hp: number) {
    const clamped = Math.max(0, hp);
    if (clamped === this.hp) return;
    const wasAlive = this.alive;
    this.hp = clamped;
    this.alive = this.hp > 0;
    this.drawHp();
    if (this.alive) {
      if (this.deathTween) { this.deathTween.stop(); this.deathTween = undefined; }
      this.setAlpha(1);
      this.sprite.setAlpha(1);
    } else if (wasAlive) {
      if (this.deathTween) this.deathTween.stop();
      this.hpBar.setVisible(false);
      this.nameTag.setVisible(false);
      this.selectionRing.setVisible(false);
      this.disableInteractive();
      if (this.hasAnim) this.playState('die');
      this.deathTween = this.scene.tweens.add({
        targets: this,
        alpha: { from: this.alpha, to: 0 },
        duration: 1000,
        ease: 'Sine.easeOut',
        onComplete: () => {
          this.deathTween = undefined;
          this.destroy();
        },
      });
    }
  }

  flashAttack() {
    this.sprite.setTint(0xff4444);
    this.scene.time.delayedCall(150, () => this.sprite.clearTint());
    if (this.hasAnim && this.alive) {
      const prevState = this.currentAnimState;
      this.playState('attack');
      this.sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        if (!this.alive) return;
        // Return to walk if still moving toward a target, otherwise idle.
        const dxT = this.targetX - this.x;
        const dyT = this.targetY - this.y;
        const moving = dxT * dxT + dyT * dyT >= 0.25;
        this.playState(moving ? 'walk' : (prevState === 'walk' ? 'walk' : 'idle'));
      });
    }
  }

  // flashHit — visual feedback khi monster bị đánh trúng (skill / basic attack).
  // Tint trắng-đỏ chớp nhanh + rung lắc (shake) nhẹ theo trục X.
  flashHit(opts: { fromX?: number; fromY?: number } = {}) {
    if (!this.alive) return;
    // Tint flash: trắng sáng → đỏ → clear, tạo cảm giác "impact".
    if (this.hitTintTimer) { this.hitTintTimer.remove(false); this.hitTintTimer = undefined; }
    this.sprite.setTintFill(0xffffff);
    this.scene.time.delayedCall(60, () => {
      if (!this.sprite || !this.sprite.scene) return;
      this.sprite.clearTint();
      this.sprite.setTint(0xff5555);
      this.hitTintTimer = this.scene.time.delayedCall(120, () => {
        if (!this.sprite || !this.sprite.scene) return;
        this.sprite.clearTint();
        this.hitTintTimer = undefined;
      });
    });

    // Knockback rung nhẹ: lệch sprite theo hướng từ attacker → monster.
    if (this.hitTween) { this.hitTween.stop(); this.sprite.x = this.spriteBaseX; this.sprite.y = this.spriteBaseY; }
    let kx = 0; let ky = 0;
    if (opts.fromX != null && opts.fromY != null) {
      const dx = this.x - opts.fromX;
      const dy = this.y - opts.fromY;
      const len = Math.hypot(dx, dy) || 1;
      kx = (dx / len) * 6;
      ky = (dy / len) * 4;
    } else {
      kx = (Math.random() < 0.5 ? -1 : 1) * 5;
    }
    const baseX = this.spriteBaseX;
    const baseY = this.spriteBaseY;
    this.hitTween = this.scene.tweens.add({
      targets: this.sprite,
      x: { from: baseX + kx, to: baseX },
      y: { from: baseY + ky, to: baseY },
      duration: 180,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.hitTween = undefined;
        if (this.sprite && this.sprite.scene) {
          this.sprite.x = baseX;
          this.sprite.y = baseY;
        }
      },
    });
  }

  setTarget(x: number, y: number) { this.targetX = x; this.targetY = y; }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.selectionRing.setVisible(selected);
  }

  private drawShadow() {
    const w = this.sprite.width * this.spriteScale * 0.7;
    const h = Math.max(4, w * 0.35);
    const cy = (this.sprite.height / 2) * this.spriteScale - h * 0.4;
    this.shadow.clear();
    this.shadow.fillStyle(0x000000, 0.35);
    this.shadow.fillEllipse(0, cy, w, h);
  }

  private drawSelectionRing() {
    const halfW = this.sprite.width / 2;
    const rx = Math.max(14, halfW * 0.8);
    const ry = Math.max(5, rx * 0.35);
    const cy = this.sprite.height / 2 - ry * 0.5;
    this.selectionRing.clear();
    this.selectionRing.lineStyle(2, 0xff3344, 1);
    this.selectionRing.strokeEllipse(0, cy, rx * 2, ry * 2);
    this.selectionRing.lineStyle(1, 0xffaaaa, 0.6);
    this.selectionRing.strokeEllipse(0, cy, rx * 2 + 4, ry * 2 + 2);
  }

  preUpdate(_time: number, delta: number) {
    this.tickInterp(delta);
  }

  tick(delta: number) {
    this.tickInterp(delta);
  }

  destroy(fromScene?: boolean) {
    if (this.auraTween) { this.auraTween.stop(); this.auraTween = undefined; }
    if (this.deathTween) { this.deathTween.stop(); this.deathTween = undefined; }
    if (this.hitTween) { this.hitTween.stop(); this.hitTween = undefined; }
    if (this.hitTintTimer) { this.hitTintTimer.remove(false); this.hitTintTimer = undefined; }
    super.destroy(fromScene);
  }

  private drawHp() {
    const w = this.isElite ? 48 : 36;
    const h = this.isElite ? 5 : 4;
    // Position HP bar above the (possibly scaled) sprite head.
    // Sprite is centered at y=0, so its top is at -spriteHeight/2 * scale.
    const spriteTop = -(this.sprite.height / 2) * this.spriteScale;
    const y = spriteTop - (this.isElite ? 12 : 8) - h;
    this.hpBar.clear();
    this.hpBar.fillStyle(0x000000, 0.7).fillRect(-w / 2, y, w, h);
    const pct = Phaser.Math.Clamp(this.hp / Math.max(1, this.maxHp), 0, 1);
    const fillColor = this.isElite ? 0x9b30ff : 0x66bb6a;
    this.hpBar.fillStyle(fillColor, 1).fillRect(-w / 2, y, w * pct, h);
    if (this.isElite) {
      this.hpBar.lineStyle(1, 0xffd700, 0.8).strokeRect(-w / 2, y, w, h);
    }
    // Keep nameTag just above the HP bar.
    this.nameTag.setY(y - 8);
  }

  // setElite — apply Elite visuals (PRD_EliteMonster §5).
  // `scale` is the elite multiplier on top of BASE_SCALE.
  setElite(isElite: boolean, scale = 1.3) {
    const targetScale = isElite ? scale * Monster.BASE_SCALE : Monster.BASE_SCALE;
    if (this.isElite === isElite && this.spriteScale === targetScale) return;
    this.isElite = isElite;
    const tpl = TPL[this.tplId];
    const baseName = `${tpl?.name ?? 'Monster'} Lv.${tpl?.level ?? '?'}`;
    if (isElite) {
      this.spriteScale = targetScale;
      this.sprite.setScale(targetScale);
      this.nameTag.setText(`[Tinh Anh] ${baseName}`);
      this.nameTag.setColor('#FFD700');
      this.nameTag.setStroke('#9B30FF', 4);
      this.drawAura();
    } else {
      this.spriteScale = Monster.BASE_SCALE;
      this.sprite.setScale(Monster.BASE_SCALE);
      this.nameTag.setText(baseName);
      this.nameTag.setColor('#ff8a80');
      this.nameTag.setStroke('#000', 3);
      if (this.aura) { this.aura.destroy(); this.aura = undefined; }
      if (this.auraTween) { this.auraTween.stop(); this.auraTween = undefined; }
    }
    // Refresh hitbox to match new sprite size.
    const w = this.sprite.width * this.spriteScale;
    const h = this.sprite.height * this.spriteScale;
    this.setSize(w, h);
    this.setInteractive(new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h), Phaser.Geom.Rectangle.Contains);
    this.drawSelectionRing();
    this.drawShadow();
    this.drawHp();
  }

  // Pulsing radial gradient aura under the monster's feet.
  private drawAura() {
    if (!this.aura) {
      this.aura = this.scene.add.graphics();
      this.addAt(this.aura, 0); // behind sprite
    }
    const g = this.aura;
    g.clear();
    const r = 28 * this.spriteScale;
    const cy = (this.sprite.height / 2) * this.spriteScale - r * 0.4;
    g.fillStyle(0xffd700, 0.35).fillCircle(0, cy, r);
    g.fillStyle(0x9b30ff, 0.25).fillCircle(0, cy, r * 0.7);
    g.lineStyle(2, 0xffd700, 0.8).strokeEllipse(0, cy, r * 2, r * 0.9);
    if (!this.auraTween) {
      this.auraTween = this.scene.tweens.add({
        targets: g,
        alpha: { from: 0.6, to: 1.0 },
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }
}
